import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppHeader from '../components/ui/AppHeader';
import CrmMapSidebar from '../components/crm-map/CrmMapSidebar';
import {
  COLLECTION_ROWS,
  computeLifecycleLayout,
  CRM_MAP_DOMAINS,
  CRM_MAP_VIEWS,
  CrmMapViewId,
  INTEGRATION_ROWS,
  isValidCrmMapView,
  lifecycleNodeById,
  LIFECYCLE_NODES,
  PUBLIC_ROUTES,
  ROLE_ROWS,
  STACK_LAYERS,
  THREE_HUBS,
} from '../utils/crmArchitectureMapData';
import '../components/pm/pm-shared.css';
import './OperationsHub.css';
import './CrmArchitectureMapPage.css';

const LIFECYCLE_LAYOUT = computeLifecycleLayout();

const CrmArchitectureMapPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view');
  const activeView: CrmMapViewId = isValidCrmMapView(viewParam) ? viewParam : 'overview';

  const selectedLifecycleId = searchParams.get('step') || 'clientWS';
  const selectedDomainId = searchParams.get('domain') || 'clientWS';

  const setView = useCallback(
    (view: CrmMapViewId) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('view', view);
        return next;
      });
    },
    [setSearchParams],
  );

  const setLifecycleStep = useCallback(
    (stepId: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('view', 'lifecycle');
        next.set('step', stepId);
        return next;
      });
    },
    [setSearchParams],
  );

  const setDomain = useCallback(
    (domainId: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('view', 'domains');
        next.set('domain', domainId);
        return next;
      });
    },
    [setSearchParams],
  );

  const viewLabel = CRM_MAP_VIEWS.find((v) => v.id === activeView)?.label ?? 'Overview';
  const selectedLifecycle = lifecycleNodeById(selectedLifecycleId) ?? lifecycleNodeById('clientWS');
  const selectedDomain = CRM_MAP_DOMAINS.find((d) => d.id === selectedDomainId) ?? CRM_MAP_DOMAINS[1];

  const viewStats = useMemo(() => {
    switch (activeView) {
      case 'lifecycle':
        return [`${LIFECYCLE_NODES.length} steps`, '1 feedback loop'];
      case 'roles':
        return [`${ROLE_ROWS.length} roles`, `${PUBLIC_ROUTES.length} public routes`];
      case 'data':
        return [`${COLLECTION_ROWS.length} collections`, '2 listener types'];
      case 'domains':
        return [`${CRM_MAP_DOMAINS.length} domains`, selectedDomain.title];
      case 'integrations':
        return [`${INTEGRATION_ROWS.length} services`, 'us-central1'];
      default:
        return ['7 roles', '3 hubs', '25+ collections'];
    }
  }, [activeView, selectedDomain.title]);

  return (
    <div className="ops-hub">
      <AppHeader />
      <div className="ops-hub-layout">
        <CrmMapSidebar activeView={activeView} onSelectView={setView} />

        <div className="ops-hub-main">
          <div className="ops-hub-toolbar">
            <div className="ops-hub-toolbar-left">
              <h2 className="ops-hub-title">{viewLabel}</h2>
              <div className="ops-hub-stats">
                {viewStats.map((stat) => (
                  <span key={stat} className="ops-stat">
                    {stat}
                  </span>
                ))}
              </div>
            </div>
            <div className="ops-hub-toolbar-right">
              <div className="ops-hub-views">
                {CRM_MAP_VIEWS.map((view) => (
                  <button
                    key={view.id}
                    type="button"
                    className={`ops-view-btn ${activeView === view.id ? 'active' : ''}`}
                    onClick={() => setView(view.id)}
                  >
                    {view.label.split(' ')[0]}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="crm-map-scroll">
            {activeView === 'overview' && <OverviewSection />}
            {activeView === 'lifecycle' && (
              <LifecycleSection
                selectedId={selectedLifecycleId}
                selected={selectedLifecycle}
                onSelect={setLifecycleStep}
              />
            )}
            {activeView === 'roles' && <RolesSection />}
            {activeView === 'data' && <DataSection />}
            {activeView === 'domains' && (
              <DomainsSection
                selectedId={selectedDomainId}
                selected={selectedDomain}
                onSelect={setDomain}
              />
            )}
            {activeView === 'integrations' && <IntegrationsSection />}
          </div>
        </div>
      </div>
    </div>
  );
};

const OverviewSection: React.FC = () => (
  <>
    <p className="crm-map-intro">
      Timber-frame construction CRM on React 18 + Firebase (lctf-projects). Three hubs organize sales,
      delivery, and external stakeholder flows.
    </p>

    <div className="crm-map-stats">
      <div className="crm-map-stat">
        <div className="crm-map-stat-value">7</div>
        <div className="crm-map-stat-label">User roles</div>
      </div>
      <div className="crm-map-stat">
        <div className="crm-map-stat-value">70+</div>
        <div className="crm-map-stat-label">Page routes</div>
      </div>
      <div className="crm-map-stat">
        <div className="crm-map-stat-value">25+</div>
        <div className="crm-map-stat-label">CRMContext collections</div>
      </div>
      <div className="crm-map-stat">
        <div className="crm-map-stat-value">~90</div>
        <div className="crm-map-stat-label">Cloud Functions</div>
      </div>
    </div>

    <section className="crm-map-section">
      <h3 className="crm-map-section-title">Application stack</h3>
      <p className="crm-map-section-sub">
        React 18 + TypeScript on Firebase Auth, Firestore, Storage · us-central1
      </p>
      {STACK_LAYERS.map((layer, index) => (
        <div key={layer.label} className="crm-map-stack-row">
          <div className="crm-map-stack-num">{index + 1}</div>
          <div className="crm-map-stack-body">
            <div className="crm-map-stack-title">
              <strong>{layer.label}</strong>
              <span className="crm-map-stack-path">{layer.sub}</span>
            </div>
            <div className="crm-map-stack-desc">{layer.desc}</div>
          </div>
        </div>
      ))}
    </section>

    <section className="crm-map-section">
      <h3 className="crm-map-section-title">Three hubs</h3>
      <div className="crm-map-hub-grid">
        {THREE_HUBS.map((hub) => (
          <article key={hub.title} className="crm-map-hub-card">
            <h4>{hub.title}</h4>
            <p>
              Routes: <code>{hub.route}</code>
            </p>
            <p>{hub.files}</p>
          </article>
        ))}
      </div>
    </section>

    <div className="crm-map-callout">
      <strong>Data access patterns</strong>
      <p>
        Pattern A: CRMContext onSnapshot (most CRM entities). Pattern B: page-level Firestore (proposals,
        portals, notifications). Pattern C: httpsCallable/fetch to Cloud Functions (secrets, releases).
        Pattern D: Firestore triggers (automation, Slack, userNotifications).
      </p>
    </div>
  </>
);

const LifecycleSection: React.FC<{
  selectedId: string;
  selected: ReturnType<typeof lifecycleNodeById>;
  onSelect: (id: string) => void;
}> = ({ selectedId, selected, onSelect }) => (
  <>
    <p className="crm-map-intro">
      Customer lifecycle from lead creation through signed proposal to project delivery and back via
      notifications. Click a node for file paths and collection details.
    </p>
    <div className="crm-map-lifecycle">
      <div className="crm-map-lifecycle-chart">
        <svg
          width={LIFECYCLE_LAYOUT.width}
          height={LIFECYCLE_LAYOUT.height}
          role="img"
          aria-label="Customer lifecycle flow diagram"
        >
          {LIFECYCLE_LAYOUT.edges.map((edge) => (
            <line
              key={`${edge.from}-${edge.to}`}
              x1={edge.x1}
              y1={edge.y1}
              x2={edge.x2}
              y2={edge.y2}
              stroke={edge.backEdge ? '#9C9889' : '#C4C0B8'}
              strokeWidth={edge.backEdge ? 1.5 : 1}
              strokeDasharray={edge.backEdge ? '5 4' : undefined}
            />
          ))}
          {LIFECYCLE_LAYOUT.nodes.map((layoutNode) => {
            const node = lifecycleNodeById(layoutNode.id);
            if (!node) return null;
            const isSelected = selectedId === layoutNode.id;
            return (
              <g
                key={layoutNode.id}
                className={`crm-map-lifecycle-node ${isSelected ? 'selected' : ''}`}
                onClick={() => onSelect(layoutNode.id)}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(layoutNode.id);
                  }
                }}
              >
                <rect
                  x={layoutNode.x}
                  y={layoutNode.y}
                  width={layoutNode.width}
                  height={layoutNode.height}
                  rx={4}
                  fill={isSelected ? undefined : '#FFFFFF'}
                  stroke={isSelected ? undefined : '#E2E0DB'}
                  strokeWidth={1}
                />
                <text
                  x={layoutNode.x + layoutNode.width / 2}
                  y={layoutNode.y + layoutNode.height / 2 + 4}
                  textAnchor="middle"
                  fill={isSelected ? '#FFFFFF' : '#1A1A1A'}
                  fontSize={11}
                  fontFamily="Inter, system-ui, sans-serif"
                  fontWeight={isSelected ? 600 : 500}
                  style={{ pointerEvents: 'none' }}
                >
                  {node.label.length > 24 ? `${node.label.slice(0, 22)}…` : node.label}
                </text>
              </g>
            );
          })}
        </svg>
        <p className="crm-map-lifecycle-caption">
          Dashed edges = feedback loops (e.g. notifications → client workspace)
        </p>
      </div>

      {selected && (
        <aside className="crm-map-detail">
          <div className="crm-map-detail-header">
            <h3>{selected.label}</h3>
            <span className="crm-map-detail-badge">Step</span>
          </div>
          <div className="crm-map-detail-body">
            <p>{selected.detail}</p>
            {selected.collection && (
              <div className="crm-map-meta">
                Collection: <code>{selected.collection}</code>
              </div>
            )}
            {selected.route && (
              <div className="crm-map-meta">
                Route: <code>{selected.route}</code>
              </div>
            )}
            {selected.file && (
              <div className="crm-map-meta">
                File: <code>{selected.file}</code>
              </div>
            )}
          </div>
        </aside>
      )}
    </div>
  </>
);

const RolesSection: React.FC = () => (
  <>
    <section className="crm-map-section">
      <h3 className="crm-map-section-title">Role-based home routes</h3>
      <p className="crm-map-section-sub">
        Defined in src/App.tsx AuthenticatedRoutes. Company email domains default to employee unless
        Firestore users/&#123;uid&#125;.role overrides.
      </p>
      <div className="crm-map-table-wrap">
        <table className="crm-map-table">
          <thead>
            <tr>
              <th>Role</th>
              <th>Home route</th>
              <th>Primary surfaces</th>
            </tr>
          </thead>
          <tbody>
            {ROLE_ROWS.map(([role, route, surfaces]) => (
              <tr key={role}>
                <td>{role}</td>
                <td className="mono">{route}</td>
                <td>{surfaces}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    <section className="crm-map-section">
      <h3 className="crm-map-section-title">Public routes (no CRMProvider)</h3>
      <p className="crm-map-section-sub">
        Anonymous auth via ensureAuth() for portal security rules. Staff CRM data is not loaded on these routes.
      </p>
      <div className="crm-map-table-wrap">
        <table className="crm-map-table">
          <thead>
            <tr>
              <th>Route</th>
              <th>Page component</th>
              <th>Purpose</th>
            </tr>
          </thead>
          <tbody>
            {PUBLIC_ROUTES.map(([route, page, purpose]) => (
              <tr key={route}>
                <td className="mono">{route}</td>
                <td>{page}</td>
                <td>{purpose}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  </>
);

const DataSection: React.FC = () => (
  <section className="crm-map-section">
    <h3 className="crm-map-section-title">Firestore collections by domain</h3>
    <p className="crm-map-section-sub">
      CRMContext = global onSnapshot on provider mount. Page-level = feature-specific queries. See
      docs/CRM_ARCHITECTURE.md for the full CRMContext inventory.
    </p>
    <div className="crm-map-table-wrap">
      <table className="crm-map-table">
        <thead>
          <tr>
            <th>Domain</th>
            <th>Collection</th>
            <th>Listener</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {COLLECTION_ROWS.map(([domain, collection, listener, notes]) => (
            <tr key={`${domain}-${collection}`}>
              <td>{domain}</td>
              <td className="mono">{collection}</td>
              <td className={listener === 'CRMContext' ? 'crm-map-listener-crm' : 'crm-map-listener-page'}>
                {listener}
              </td>
              <td>{notes}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

const DomainsSection: React.FC<{
  selectedId: string;
  selected: (typeof CRM_MAP_DOMAINS)[number];
  onSelect: (id: string) => void;
}> = ({ selectedId, selected, onSelect }) => (
  <>
    <p className="crm-map-intro">
      Select a domain to see its pages, Firestore collections, utilities, and cross-feature connections.
    </p>
    <div className="crm-map-pill-row">
      {CRM_MAP_DOMAINS.map((domain) => (
        <button
          key={domain.id}
          type="button"
          className={`crm-map-domain-pill ${selectedId === domain.id ? 'active' : ''}`}
          onClick={() => onSelect(domain.id)}
        >
          {domain.title}
        </button>
      ))}
    </div>
    <article className="crm-map-domain-panel">
      <div className="crm-map-domain-panel-header">{selected.title}</div>
      <div className="crm-map-domain-panel-body">
        <div>
          <div className="crm-map-domain-block">
            <h4>Pages</h4>
            <p>{selected.pages}</p>
          </div>
          <div className="crm-map-domain-block">
            <h4>Collections</h4>
            <p>
              <code>{selected.collections}</code>
            </p>
          </div>
        </div>
        <div>
          <div className="crm-map-domain-block">
            <h4>Key utils</h4>
            <p>
              <code>{selected.utils}</code>
            </p>
          </div>
          <div className="crm-map-domain-block">
            <h4>Connects to</h4>
            <p>{selected.connects}</p>
          </div>
        </div>
      </div>
    </article>
  </>
);

const IntegrationsSection: React.FC = () => (
  <section className="crm-map-section">
    <h3 className="crm-map-section-title">External integrations</h3>
    <p className="crm-map-section-sub">
      Cloud Functions deploy to us-central1-lctf-projects.cloudfunctions.net. Secrets live in
      settings/app_settings; public_settings mirrors safe keys.
    </p>
    <div className="crm-map-table-wrap">
      <table className="crm-map-table">
        <thead>
          <tr>
            <th>Service</th>
            <th>Client touchpoints</th>
            <th>Cloud Functions</th>
          </tr>
        </thead>
        <tbody>
          {INTEGRATION_ROWS.map(([service, touchpoints, functions]) => (
            <tr key={service}>
              <td>{service}</td>
              <td>{touchpoints}</td>
              <td className="mono">{functions}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

export default CrmArchitectureMapPage;
